// Must stay first: sets Zod's `jitless` flag before any schema module evaluates,
// so Zod never runs its CSP-blocked `new Function` eval probe. See the module.
import "@/utils/zod-jitless";
import ReactDOM from "react-dom/client";
import { extractLazySectionTypes, preloadSectionComponents } from "@/registry/section-lazy-loaders";
import type { VikePageContext } from "@/types/config";
import { readStorage, writeStorage } from "@/utils/safe-storage";
import { initSentry, reportHydrationError } from "@/utils/sentry";
import { PageShell } from "./PageShell";
import "../styles/global.css";

export { onRenderClient };

// Recover from a failed page-chunk dynamic import (stale deploy after a redeploy,
// or a flaky mobile-Safari network). Vite fires `vite:preloadError`; left
// unhandled the user is stranded on a dead page and cannot register. Reload once
// to pull the current chunk manifest — timestamp-guarded so it can never loop.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    const KEY = "vike-preload-reload-at";
    const last = Number(readStorage("session", KEY) || 0);
    if (Date.now() - last < 10_000) return;
    writeStorage("session", KEY, String(Date.now()));
    window.location.reload();
  });
}

let root: ReactDOM.Root | null = null;

async function onRenderClient(pageContext: VikePageContext) {
  initSentry();

  const { Page, pageProps } = pageContext;

  // Fetch + resolve the page's code-split section chunks BEFORE hydrate/render
  // so the synchronous render below matches the server HTML (no Suspense
  // boundary, no hydration mismatch). On a chunk-fetch failure the
  // `vite:preloadError` handler above reloads once to pull the current manifest.
  const config = (pageProps as { config?: unknown } | undefined)?.config;
  await preloadSectionComponents(extractLazySectionTypes(config));

  const container = document.getElementById("app");
  if (!container) throw new Error("Could not find app container");

  const page = (
    <PageShell pageContext={pageContext}>
      <Page {...pageProps} />
    </PageShell>
  );

  if (pageContext.isHydration) {
    root = ReactDOM.hydrateRoot(container, page, {
      onRecoverableError: (error, errorInfo) => {
        reportHydrationError(error, { componentStack: errorInfo.componentStack });
      },
    });
  } else {
    // Navigation (client-side routing)
    if (!root) {
      root = ReactDOM.createRoot(container);
    }
    root.render(page);

    // The <title> is written once, server-side, by +onRenderHtml.tsx. Vike does
    // not re-apply the head on a soft navigation, so without this the tab keeps
    // the PREVIOUS page's title while the new page's content renders. Set it
    // before onPageTransitionEnd fires, so anything reading the title after a
    // navigation sees the current page.
    // Only `title` is re-applied: the rest of the head (description, og:*,
    // robots, structured data) matters to crawlers, which always do a full page
    // load and therefore get the correct server-rendered values.
    const title = (pageProps as { title?: unknown } | undefined)?.title;
    if (typeof title === "string" && title !== "") {
      document.title = title;
    }
  }

  // Report Web Vitals
  if (typeof window !== "undefined" && pageContext.exports?.reportWebVitals) {
    const { onCLS, onFCP, onLCP, onTTFB } = await import("web-vitals");

    onCLS(console.log);
    onFCP(console.log);
    onLCP(console.log);
    onTTFB(console.log);
  }
}
